import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const DiscordIcon = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.5 4.5c-1.5-.7-3.1-1.2-4.8-1.5-.2.4-.5 1-.7 1.4-1.8-.3-3.6-.3-5.3 0-.2-.4-.5-1-.7-1.4C6.3 3.3 4.7 3.8 3.2 4.5 0 9.3-.8 14 .1 18.6c2 1.5 3.9 2.4 5.8 3 .5-.6.9-1.3 1.3-2-.7-.3-1.4-.6-2-1 .2-.1.3-.2.5-.3 3.9 1.8 8.1 1.8 12 0 .2.1.3.2.5.3-.6.4-1.3.7-2 1 .4.7.8 1.4 1.3 2 1.9-.6 3.8-1.5 5.8-3 1-5.3-.3-9.9-3.8-14.1zM8.5 15.9c-1.1 0-2-1-2-2.3s.9-2.3 2-2.3 2 1 2 2.3-.9 2.3-2 2.3zm7 0c-1.1 0-2-1-2-2.3s.9-2.3 2-2.3 2 1 2 2.3-.9 2.3-2 2.3z"/>
  </svg>
);

export default function DiscordLinkButton({ variant = 'success' }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!user?.is_vip) return null;

  const linked = !!user.discord_id;

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/discord/link-url', { params: { origin: window.location.origin } });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Could not start Discord link');
      setBusy(false);
    }
  };

  if (linked) {
    return (
      <div
        className={`mt-6 flex items-center justify-center gap-3 rounded-2xl border border-[#5865F2]/30 bg-[#5865F2]/[0.08] px-5 py-4`}
        data-testid="discord-linked-badge"
      >
        <div className="h-8 w-8 rounded-full bg-[#5865F2]/20 flex items-center justify-center text-[#7B85F5]">
          <Check className="h-4 w-4" />
        </div>
        <div className="text-sm text-white/90 text-left">
          <div className="font-semibold">Discord linked · VIP role active</div>
          <div className="text-white/50 text-xs font-mono truncate">{user.discord_username}</div>
        </div>
      </div>
    );
  }

  return (
    <button
      data-testid="link-discord-btn"
      onClick={start}
      disabled={busy}
      className={`mt-6 w-full inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-[#5865F2] text-white font-semibold text-lg hover:shadow-[0_0_44px_rgba(88,101,242,0.7)] transition-shadow disabled:opacity-60 ${variant === 'success' ? '' : 'py-3'}`}
    >
      <DiscordIcon />
      {busy ? 'Opening Discord…' : 'Get Access'}
    </button>
  );
}
